import {
  HarmReport,
  InterventionDecision,
  InterventionStatus,
} from '@inplay/models';
import { Option } from '../../component/dropDownInput/dropDownInput.type';
import {
  FilterFieldConfig,
  FilterFieldType,
} from '../../component/filterPanel/filterPanel.types';
import { OptionGroup } from '../../component/multiSelectDropdown/multiSelectDropdown.type';
import {
  lengthOfPlayOptions,
  moneyOptions,
  behaviourOptions,
  otherOptions,
} from '../../component/reportDetails/constants';
import { isAutoGeneratedReport } from '../../component/harmReportForm/harmReportFormHelper';

export const AUTO_GENERATED_CREATED_BY = '__AUTO_GENERATED__';

export const getHarmInstanceOptions = (
  t: (key: string) => string,
): Option[] => [
  { label: t('harmReports.filters.harmInstance.1st'), value: '1' },
  { label: t('harmReports.filters.harmInstance.2nd'), value: '2' },
  { label: t('harmReports.filters.harmInstance.3rd'), value: '3' },
  { label: t('harmReports.filters.harmInstance.4th'), value: '4' },
  { label: t('harmReports.filters.harmInstance.5th'), value: '5' },
  { label: t('harmReports.filters.harmInstance.6th'), value: '6' },
  { label: t('harmReports.filters.harmInstance.7th'), value: '7' },
  { label: t('harmReports.filters.harmInstance.8th'), value: '8' },
  { label: t('harmReports.filters.harmInstance.9th'), value: '9' },
  { label: t('harmReports.filters.harmInstance.10+'), value: '10+' },
];

export const getInterventionDecisionOptions = (
  t: (key: string) => string,
): Option[] => [
  {
    label: t('harmReports.filters.interventionDecision.required'),
    value: InterventionDecision.REQUIRED,
  },
  {
    label: t('harmReports.filters.interventionDecision.notRequired'),
    value: InterventionDecision.NOT_REQUIRED,
  },
  {
    label: t('harmReports.filters.interventionDecision.pending'),
    value: InterventionDecision.PENDING,
  },
];

export const getInterventionStatusOptions = (
  t: (key: string) => string,
): Option[] => [
  {
    label: t('harmReports.filters.interventionStatus.pending'),
    value: InterventionStatus.PENDING,
  },
  {
    label: t('harmReports.filters.interventionStatus.complete'),
    value: InterventionStatus.COMPLETE,
  },
  {
    label: t('harmReports.filters.interventionStatus.missed'),
    value: InterventionStatus.MISSED,
  },
  {
    label: t('harmReports.filters.interventionStatus.na'),
    value: InterventionStatus.NOT_APPLICABLE,
  },
];

export const getDeadlineOptions = (t: (key: string) => string): Option[] => [
  { label: t('harmReports.filters.deadline.1h'), value: '1' },
  { label: t('harmReports.filters.deadline.2h'), value: '2' },
  { label: t('harmReports.filters.deadline.3h'), value: '3' },
  { label: t('harmReports.filters.deadline.4h'), value: '4' },
  { label: t('harmReports.filters.deadline.5h'), value: '5' },
  { label: t('harmReports.filters.deadline.6h+'), value: '6+' },
];

export const getTotalInterventionsOptions = (
  t: (key: string) => string,
): Option[] => [
  { label: '1', value: '1' },
  { label: '2', value: '2' },
  { label: '3', value: '3' },
  { label: '4', value: '4' },
  { label: '5', value: '5' },
  { label: '6', value: '6' },
  { label: '7', value: '7' },
  { label: '8', value: '8' },
  { label: t('harmReports.filters.totalInterventions.9+'), value: '9+' },
];

export const getHarmObservedGroups = (
  t: (key: string) => string,
): OptionGroup[] => [
  {
    groupLabel: t('harmReports.filters.harmObserved.lengthOfPlay'),
    options: lengthOfPlayOptions,
  },
  {
    groupLabel: t('harmReports.filters.harmObserved.money'),
    options: moneyOptions,
  },
  {
    groupLabel: t('harmReports.filters.harmObserved.behaviour'),
    options: behaviourOptions,
  },
  {
    groupLabel: t('harmReports.filters.harmObserved.other'),
    options: otherOptions,
  },
];

export const getPlayerNoOptions = (reports: HarmReport[]): Option[] => {
  const uniquePlayerIds: Set<string> = new Set();
  reports.forEach((report: HarmReport) => {
    if (report.playerProfile?.id) {
      uniquePlayerIds.add(report.playerProfile.id);
    }
  });
  return Array.from(uniquePlayerIds).map((playerId: string) => ({
    label: playerId.slice(0, 5),
    value: playerId,
  }));
};

export const getCreatedByOptions = (
  reports: HarmReport[],
  t: (key: string) => string,
): Option[] => {
  const uniqueNames: Set<string> = new Set();
  let hasAutoGenerated: boolean = false;
  reports.forEach((report: HarmReport) => {
    if (isAutoGeneratedReport(report)) {
      hasAutoGenerated = true;
    } else if (report.createdBy?.firstName) {
      const fullName: string = `${report.createdBy.firstName} ${report.createdBy.lastName}`;
      uniqueNames.add(fullName);
    }
  });

  const manualOptions: Option[] = Array.from(uniqueNames).map(
    (name: string) => ({ label: name, value: name }),
  );

  if (hasAutoGenerated) {
    return [
      {
        label: t('reportDetails.autogeneratedReport'),
        value: AUTO_GENERATED_CREATED_BY,
      },
      ...manualOptions,
    ];
  }
  return manualOptions;
};

export const getHarmReportGeneralFilterFields = (
  reports: HarmReport[],
  t: (key: string) => string,
): FilterFieldConfig[] => [
  {
    key: 'sectionGeneral',
    label: t('harmReports.filters.labels.sectionGeneral'),
    type: FilterFieldType.SECTION_HEADER,
  },
  {
    key: ['dateCreatedFrom', 'dateCreatedTo'],
    label: t('harmReports.filters.labels.dateCreated'),
    type: FilterFieldType.DATE_RANGE,
  },
  {
    key: ['timeCreatedFrom', 'timeCreatedUntil'],
    label: t('harmReports.filters.labels.timeCreated'),
    type: FilterFieldType.TIME_RANGE,
  },
  {
    key: 'playerNo',
    label: t('harmReports.filters.labels.playerNo'),
    type: FilterFieldType.SELECT,
    options: getPlayerNoOptions(reports),
    search: true,
    placeholder: t('userManagement.select'),
  },
  {
    key: 'harmObserved',
    label: t('harmReports.filters.labels.harmObserved'),
    type: FilterFieldType.GROUPED_MULTI_SELECT,
    groups: getHarmObservedGroups(t),
    search: true,
    placeholder: t('userManagement.select'),
  },
  {
    key: 'harmInstance',
    label: t('harmReports.filters.labels.harmInstance'),
    type: FilterFieldType.SELECT,
    options: getHarmInstanceOptions(t),
    placeholder: t('userManagement.select'),
  },
  {
    key: 'interventionDecision',
    label: t('harmReports.filters.labels.interventionDecision'),
    type: FilterFieldType.MULTI_SELECT,
    options: getInterventionDecisionOptions(t),
    placeholder: t('userManagement.select'),
  },
  {
    key: 'interventionStatus',
    label: t('harmReports.filters.labels.interventionStatus'),
    type: FilterFieldType.MULTI_SELECT,
    options: getInterventionStatusOptions(t),
    placeholder: t('userManagement.select'),
  },
  {
    key: 'reviewDeadline',
    label: t('harmReports.filters.labels.reviewDeadline'),
    type: FilterFieldType.SELECT,
    options: getDeadlineOptions(t),
    placeholder: t('userManagement.select'),
  },
  {
    key: 'interventionDeadline',
    label: t('harmReports.filters.labels.interventionDeadline'),
    type: FilterFieldType.SELECT,
    options: getDeadlineOptions(t),
    placeholder: t('userManagement.select'),
  },
  {
    key: 'totalInterventionsOffered',
    label: t('harmReports.filters.labels.totalInterventionsOffered'),
    type: FilterFieldType.SELECT,
    options: getTotalInterventionsOptions(t),
    placeholder: t('userManagement.select'),
  },
  {
    key: 'totalInterventionsRequested',
    label: t('harmReports.filters.labels.totalInterventionsRequested'),
    type: FilterFieldType.SELECT,
    options: getTotalInterventionsOptions(t),
    placeholder: t('userManagement.select'),
  },
  {
    key: 'createdBy',
    label: t('harmReports.filters.labels.createdBy'),
    type: FilterFieldType.SELECT,
    options: getCreatedByOptions(reports, t),
    search: true,
    placeholder: t('userManagement.select'),
  },
  {
    key: 'sectionManagerReview',
    label: t('harmReports.filters.labels.sectionManagerReview'),
    type: FilterFieldType.SECTION_HEADER,
    showDivider: true,
  },
  {
    key: 'reviewedBy',
    label: t('harmReports.filters.labels.reviewedBy'),
    type: FilterFieldType.SELECT,
    options: getReviewedByOptions(reports),
    search: true,
    placeholder: t('userManagement.select'),
  },
  {
    key: ['dateReviewedFrom', 'dateReviewedTo'],
    label: t('harmReports.filters.labels.dateReviewed'),
    type: FilterFieldType.DATE_RANGE,
  },
  {
    key: 'reviewStatus',
    label: t('harmReports.filters.labels.reviewStatus'),
    type: FilterFieldType.MULTI_SELECT,
    options: getReviewStatusOptions(t),
    placeholder: t('userManagement.select'),
  },
];

export const getReviewStatusOptions = (
  t: (key: string) => string,
): Option[] => [
  {
    label: t('harmReports.filters.interventionStatus.pending'),
    value: 'Pending',
  },
  {
    label: t('harmReports.filters.interventionStatus.complete'),
    value: 'Complete',
  },
  {
    label: t('harmReports.filters.interventionStatus.missed'),
    value: 'Missed',
  },
  { label: t('harmReports.filters.interventionStatus.na'), value: 'N/A' },
];

export const getReviewedByOptions = (reports: HarmReport[]): Option[] => {
  const uniqueNames: Set<string> = new Set();
  reports.forEach((report: HarmReport) => {
    if (report.reviewedBy?.firstName) {
      const fullName: string = `${report.reviewedBy.firstName} ${report.reviewedBy.lastName}`;
      uniqueNames.add(fullName);
    }
  });
  return Array.from(uniqueNames).map((name: string) => ({
    label: name,
    value: name,
  }));
};
